import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef, useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { PLANS, HOW_IT_WORKS_STEPS, ROUTE_PATHS } from "@/lib/index";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Download, Smartphone, X, Share, Send, Mail } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useThemeContext } from "@/hooks/useTheme";

/* ══════════════════════════════════════════════════════════
   Modal — Napisz do nas
══════════════════════════════════════════════════════════ */
function ContactModal({ onClose }: { onClose: () => void }) {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent]       = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    // Otwórz klienta pocztowego z wypełnionymi polami
    const subject = encodeURIComponent(`Wiadomość od ${name}`);
    const body    = encodeURIComponent(`Imię: ${name}\nEmail zwrotny: ${email}\n\n${message}`);
    window.location.href = `mailto:biuro@e-grob.pl?subject=${subject}&body=${body}`;
    setTimeout(() => { setSending(false); setSent(true); }, 800);
  };

  const inputStyle: React.CSSProperties = {
    background: 'rgba(212,175,55,0.06)',
    border: '1px solid rgba(212,175,55,0.25)',
    borderRadius: '8px',
    color: 'rgba(255,235,190,0.92)',
    padding: '10px 14px',
    width: '100%',
    outline: 'none',
    fontSize: '0.95rem',
    transition: 'border-color 0.2s',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,6,20,0.82)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.96 }}
        animate={{ y: 0,  opacity: 1, scale: 1    }}
        exit={{    y: 20, opacity: 0, scale: 0.97  }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(32,38,80,0.98) 0%, rgba(10,12,32,0.99) 100%)',
          border: '1px solid rgba(212,175,55,0.30)',
          borderRadius: '18px',
          boxShadow: '0 0 60px rgba(212,175,55,0.12), 0 24px 60px rgba(0,0,0,0.60)',
          maxWidth: '460px',
          width: '100%',
          padding: '36px 32px 32px',
          position: 'relative',
        }}
      >
        {/* Zamknij */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(212,175,55,0.10)',
            border: '1px solid rgba(212,175,55,0.20)',
            borderRadius: '50%', width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'rgba(212,175,55,0.70)',
            transition: 'background 0.2s',
          }}
        >
          <X size={16} />
        </button>

        {/* Ikona + tytuł */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{
            background: 'rgba(212,175,55,0.14)',
            border: '1px solid rgba(212,175,55,0.30)',
            borderRadius: '10px', padding: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Mail size={20} style={{ color: 'rgba(212,175,55,0.90)' }} />
          </div>
          <h2 style={{ color: 'rgba(212,175,55,0.95)', fontSize: '1.35rem', fontWeight: 700, letterSpacing: '0.02em' }}>
            Napisz do nas
          </h2>
        </div>
        <p style={{ color: 'rgba(200,185,150,0.65)', fontSize: '0.85rem', marginBottom: 24 }}>
          biuro@e-grob.pl
        </p>

        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: 'center', padding: '24px 0' }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✉️</div>
            <p style={{ color: 'rgba(212,175,55,0.90)', fontWeight: 600, fontSize: '1.05rem' }}>
              Klient pocztowy otwarty!
            </p>
            <p style={{ color: 'rgba(200,185,150,0.60)', fontSize: '0.85rem', marginTop: 6 }}>
              Wyślij wiadomość ze swojego klienta poczty.
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: 20,
                background: 'rgba(212,175,55,0.15)',
                border: '1px solid rgba(212,175,55,0.35)',
                borderRadius: '8px', padding: '8px 24px',
                color: 'rgba(212,175,55,0.90)', cursor: 'pointer', fontSize: '0.9rem',
              }}
            >Zamknij</button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ color: 'rgba(212,175,55,0.70)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Imię i nazwisko</label>
              <input
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Jan Kowalski"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: 'rgba(212,175,55,0.70)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Twój e-mail</label>
              <input
                required
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="jan@example.com"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ color: 'rgba(212,175,55,0.70)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Wiadomość</label>
              <textarea
                required
                rows={4}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Napisz do nas..."
                style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              style={{
                marginTop: 4,
                background: sending ? 'rgba(212,175,55,0.20)' : 'linear-gradient(135deg, rgba(212,175,55,0.85) 0%, rgba(180,130,30,0.90) 100%)',
                border: '1px solid rgba(212,175,55,0.45)',
                borderRadius: '10px',
                padding: '12px 24px',
                color: sending ? 'rgba(212,175,55,0.50)' : 'rgba(10,8,2,0.95)',
                fontWeight: 700, fontSize: '0.95rem',
                cursor: sending ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.2s',
                letterSpacing: '0.04em',
              }}
            >
              <Send size={16} />
              {sending ? 'Otwieranie...' : 'Wyślij wiadomość'}
            </button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Przycisk / modal instalacji PWA ── */
function PWAInstallButton() {
  const { state, install } = usePWAInstall();
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  // Nie pokazuj nic gdy nie ma co pokazać
  if (state === 'installed' || state === 'unsupported' || state === 'dismissed') return null;

  /* iOS — przycisk otwiera instrukcję */
  if (state === 'ios') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, ...springPresets.gentle }}
        >
          <Button
            size="lg"
            onClick={() => setShowIOSGuide(true)}
            className="relative px-8 py-6 text-lg font-medium overflow-hidden group"
            style={{
              background: 'rgba(var(--theme-accent-rgb),0.08)',
              border: '1px solid rgba(var(--theme-accent-rgb),0.35)',
              color: 'var(--theme-visits-accent)',
            }}
          >
            <Smartphone className="w-5 h-5 mr-2.5 group-hover:scale-110 transition-transform" />
            Dodaj do ekranu
          </Button>
        </motion.div>

        {/* Modal iOS */}
        <AnimatePresence>
          {showIOSGuide && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
              onClick={() => setShowIOSGuide(false)}
            >
              <motion.div
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 60, opacity: 0 }}
                transition={springPresets.snappy}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm rounded-3xl p-6 relative"
                style={{
                  background: 'var(--theme-panel-bg)',
                  border: '1px solid var(--theme-visits-border)',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
                }}
              >
                <button
                  onClick={() => setShowIOSGuide(false)}
                  className="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
                  style={{ color: 'var(--theme-text-dim)' }}
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Ikonka */}
                <div className="flex justify-center mb-5">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ background: 'rgba(var(--theme-accent-rgb),0.10)', border: '1px solid rgba(var(--theme-accent-rgb),0.25)' }}>
                    <span className="text-3xl">🪦</span>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-center mb-1"
                  style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-visits-accent)' }}>
                  Zainstaluj e-grób
                </h3>
                <p className="text-sm text-center mb-6" style={{ color: 'var(--theme-text-muted)' }}>
                  Dodaj aplikację do ekranu głównego
                </p>

                <div className="space-y-3">
                  {[
                    { icon: <Share className="w-4 h-4" />, text: 'Kliknij ikonę Udostępnij w pasku Safari' },
                    { icon: <span className="text-sm font-bold">+</span>, text: 'Wybierz „Dodaj do ekranu głównego"' },
                    { icon: <span className="text-sm">✓</span>, text: 'Kliknij „Dodaj" w prawym górnym rogu' },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{ background: 'rgba(var(--theme-accent-rgb),0.05)', border: '1px solid rgba(var(--theme-accent-rgb),0.1)' }}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(var(--theme-accent-rgb),0.12)', color: 'var(--theme-visits-accent)' }}>
                        {step.icon}
                      </div>
                      <span className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>{step.text}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-center">
                  <p className="text-xs" style={{ color: 'var(--theme-text-dim)' }}>
                    Aplikacja działa bez połączenia z internetem
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  /* Android/Desktop — natywny prompt */
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, ...springPresets.gentle }}
    >
      <Button
        size="lg"
        onClick={install}
        disabled={state === 'installing'}
        className="relative px-8 py-6 text-lg font-medium overflow-hidden group"
        style={{
          background: state === 'installing'
            ? 'rgba(var(--theme-accent-rgb),0.05)'
            : 'rgba(var(--theme-accent-rgb),0.08)',
          border: '1px solid rgba(var(--theme-accent-rgb),0.35)',
          color: 'var(--theme-visits-accent)',
        }}
      >
        {/* Złoty shimmer przy hover */}
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: 'linear-gradient(135deg, transparent 30%, rgba(var(--theme-accent-rgb),0.06) 50%, transparent 70%)' }} />
        <Download className={`w-5 h-5 mr-2.5 transition-transform ${state === 'installing' ? 'animate-bounce' : 'group-hover:scale-110'}`} />
        {state === 'installing' ? 'Instalowanie…' : 'Zainstaluj aplikację'}
      </Button>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════════
   Animacja canvas — konstelacje (tylko motyw classic)
══════════════════════════════════════════════════════════ */
interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  radius: number;
  alpha: number;
  pulse: number;
  pulseSpeed: number;
}

function ConstellationCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const init = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return () => {};

    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const COUNT = Math.floor((canvas.width * canvas.height) / 10000);
    const MAX_DIST = 175;
    const GOLD  = { r: 212, g: 175, b: 55 };
    const GOLD2 = { r: 255, g: 218, b: 90 };

    const particles: Particle[] = Array.from({ length: COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      radius: Math.random() * 2.0 + 0.8,
      alpha: Math.random() * 0.55 + 0.45,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.018 + 0.006,
    }));

    // Kilka większych, jasnych "gwiazd"
    const STARS = Math.floor(COUNT * 0.12);
    for (let i = 0; i < STARS; i++) {
      particles[i].radius = Math.random() * 2.8 + 1.4;
      particles[i].alpha  = Math.random() * 0.6 + 0.5;
    }

    let rafId: number;
    let tick = 0;

    function draw() {
      rafId = requestAnimationFrame(draw);
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
      tick++;

      // Linie między bliskimi cząsteczkami
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const lineAlpha = (1 - dist / MAX_DIST) * 0.30;
            ctx!.beginPath();
            ctx!.moveTo(particles[i].x, particles[i].y);
            ctx!.lineTo(particles[j].x, particles[j].y);
            ctx!.strokeStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},${lineAlpha})`;
            ctx!.lineWidth = 0.6;
            ctx!.stroke();
          }
        }
      }

      // Cząsteczki
      for (const p of particles) {
        p.pulse += p.pulseSpeed;
        const glow = Math.sin(p.pulse) * 0.25 + 0.75; // 0.5–1.0
        const alpha = p.alpha * glow;

        // Halo
        const grad = ctx!.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 4);
        grad.addColorStop(0, `rgba(${GOLD2.r},${GOLD2.g},${GOLD2.b},${alpha * 0.55})`);
        grad.addColorStop(1, `rgba(${GOLD.r},${GOLD.g},${GOLD.b},0)`);
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.radius * 4, 0, Math.PI * 2);
        ctx!.fillStyle = grad;
        ctx!.fill();

        // Rdzeń
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${GOLD2.r},${GOLD2.g},${GOLD2.b},${alpha})`;
        ctx!.fill();

        // Ruch
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      }

      // Co jakiś czas losowy "shooting star"
      if (tick % 280 === 0) {
        const sx = Math.random() * canvas.width;
        const sy = Math.random() * canvas.height * 0.6;
        const len = Math.random() * 120 + 60;
        const angle = Math.random() * 0.4 + 0.1;
        const sg = ctx!.createLinearGradient(sx, sy, sx + len * Math.cos(angle), sy + len * Math.sin(angle));
        sg.addColorStop(0, 'rgba(255,220,100,0)');
        sg.addColorStop(0.4, 'rgba(255,220,100,0.7)');
        sg.addColorStop(1, 'rgba(255,220,100,0)');
        ctx!.beginPath();
        ctx!.moveTo(sx, sy);
        ctx!.lineTo(sx + len * Math.cos(angle), sy + len * Math.sin(angle));
        ctx!.strokeStyle = sg;
        ctx!.lineWidth = 1.5;
        ctx!.stroke();
      }
    }

    draw();
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cleanup = init(canvas);

    const ro = new ResizeObserver(() => {
      cleanup();
      cleanup = init(canvas);
    });
    ro.observe(canvas);
    return () => { cleanup(); ro.disconnect(); };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.85 }}
    />
  );
}

function HeroSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const { current: theme } = useThemeContext();
  const isClassic = theme.id === 'classic';
  const [contactOpen, setContactOpen] = useState(false);
  // brak refs — nowe podejście z dwoma PNG nie wymaga refów

  // Wybór zdjęcia i nakładki zależnie od motywu (tylko graphite i soft)
  const heroImg = theme.id === 'graphite'
    ? IMAGES.HERO_GRAPHITE_1
    : IMAGES.HERO_DAWN_1;
  const heroOpacity = theme.id === 'graphite' ? '0.38' : '0.72';
  const heroOverlay = theme.id === 'graphite'
    ? 'linear-gradient(to bottom, rgba(8,10,18,0.70) 0%, rgba(8,10,18,0.35) 45%, rgba(8,10,18,0.80) 100%)'
    : 'linear-gradient(to bottom, rgba(18,8,2,0.55) 0%, rgba(18,8,2,0.18) 45%, rgba(18,8,2,0.70) 100%)';
  const isSoft = theme.id === 'soft';
  const heroTitleColor = isSoft ? 'rgba(255,245,235,0.96)' : 'rgba(212,175,55,0.78)';
  const heroTitleShadow = isSoft
    ? '0 2px 40px rgba(12,5,2,0.80), 0 0 80px rgba(232,135,74,0.20), 0 4px 8px rgba(0,0,0,0.60)'
    : isClassic
      ? '0 0 60px rgba(212,175,55,0.30), 0 0 120px rgba(212,175,55,0.12)'
      : '0 0 80px rgba(212,175,55,0.15)';

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {isClassic ? (
          /* ── Motyw classic: animacja konstelacji ── */
          <>
            {/* Jaśniejsze tło — głęboki granat zamiast czerni */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(32,38,80,1) 0%, rgba(16,20,52,1) 55%, rgba(8,10,30,1) 100%)'
            }} />
            {/* Wyraźna mgławica — złota + fioletowa poświata */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 28% 55%, rgba(212,175,55,0.12) 0%, transparent 48%), radial-gradient(ellipse at 74% 28%, rgba(130,100,255,0.11) 0%, transparent 46%), radial-gradient(ellipse at 55% 78%, rgba(180,140,50,0.07) 0%, transparent 38%)'
            }} />
            <ConstellationCanvas />
            {/* Unoszący się szkielet astronauta — styl konstelacji */}
            <style>{`
              @keyframes astronautFloat {
                0%   { transform: translateY(0px)   rotate(-1deg); }
                30%  { transform: translateY(-14px) rotate(0.5deg); }
                60%  { transform: translateY(-20px) rotate(1deg);  }
                80%  { transform: translateY(-8px)  rotate(0.2deg); }
                100% { transform: translateY(0px)   rotate(-1deg); }
              }
              @keyframes astronautPulse {
                0%   { opacity: 0.52; filter: drop-shadow(0 0 10px rgba(212,175,55,0.40)) drop-shadow(0 0 22px rgba(212,175,55,0.18)); }
                35%  { opacity: 0.72; filter: drop-shadow(0 0 26px rgba(212,175,55,0.72)) drop-shadow(0 0 50px rgba(212,175,55,0.28)); }
                65%  { opacity: 0.62; filter: drop-shadow(0 0 18px rgba(212,175,55,0.55)) drop-shadow(0 0 36px rgba(255,210,80,0.22)); }
                100% { opacity: 0.52; filter: drop-shadow(0 0 10px rgba(212,175,55,0.40)) drop-shadow(0 0 22px rgba(212,175,55,0.18)); }
              }
              @keyframes invitePulse {
                0%   { filter: drop-shadow(0 0 8px rgba(212,175,55,0.30)); }
                50%  { filter: drop-shadow(0 0 28px rgba(212,175,55,0.70)) drop-shadow(0 0 50px rgba(255,200,60,0.30)); }
                100% { filter: drop-shadow(0 0 8px rgba(212,175,55,0.30)); }
              }
              .skeleton-btn:hover .skeleton-tooltip { opacity: 1 !important; }
              .skeleton-btn:hover .skeleton-img    { filter: drop-shadow(0 0 32px rgba(212,175,55,0.95)) drop-shadow(0 0 12px rgba(255,210,80,0.70)) !important; }
            `}</style>
            <div className="absolute inset-0" style={{ zIndex: 4, pointerEvents: 'none' }}>
              <button
                onClick={() => setContactOpen(true)}
                title="Napisz do nas!"
                className="skeleton-btn"
                style={{
                  position: 'absolute',
                  right: '7%',
                  top: '12%',
                  width: 'clamp(150px, 16vw, 260px)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  pointerEvents: 'all',
                  animation: 'astronautFloat 8s ease-in-out infinite, astronautPulse 5.5s ease-in-out infinite, invitePulse 3s ease-in-out infinite',
                  animationDelay: '0s, 1.2s, 0.5s',
                }}
              >
                {/* Tooltip */}
                <span style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 10px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(10,12,32,0.90)',
                  border: '1px solid rgba(212,175,55,0.35)',
                  borderRadius: '8px',
                  padding: '5px 12px',
                  color: 'rgba(212,175,55,0.90)',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                }} className="skeleton-tooltip">
                  ✉️ Napisz do nas
                </span>

                {/* Szkielet astronauty — animowany WebP (machanie ręką) */}
                <img
                  src="/images/skeleton_waving_anim.webp"
                  alt="Napisz do nas"
                  className="skeleton-img"
                  style={{ width: '100%', display: 'block', transition: 'filter 0.3s' }}
                />
              </button>
            </div>
            {/* Gradient fade góra/dół */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(8,10,30,0.45) 0%, transparent 20%, transparent 78%, rgba(8,10,30,0.65) 100%)',
              zIndex: 3
            }} />
          </>
        ) : (
          /* ── Motyw graphite / soft: zdjęcie ── */
          <>
            <img
              src={heroImg}
              alt=""
              className="w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: heroOpacity }}
            />
            <div className="absolute inset-0" style={{ background: heroOverlay }} />
          </>
        )}
      </div>

      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="relative z-10 container mx-auto px-4 text-center py-24"
      >
        <motion.div variants={staggerItem} className="flex justify-center mb-6">
          <div className="w-20 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        </motion.div>
        <motion.h1
          variants={staggerItem}
          className="font-heading text-7xl md:text-8xl lg:text-9xl font-bold mb-6 tracking-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: heroTitleColor,
            textShadow: heroTitleShadow,
          }}
        >
          e-grób
        </motion.h1>

        <motion.p
          variants={staggerItem}
          className="text-2xl md:text-3xl mb-4 font-light tracking-wide"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
            color: isSoft ? 'rgba(255,240,218,0.90)' : undefined,
          }}
        >
          Zostaw coś po sobie.
        </motion.p>

        <motion.p
          variants={staggerItem}
          className="text-lg md:text-xl max-w-2xl mx-auto mb-12"
          style={{
            color: isSoft ? 'rgba(255,235,205,0.94)' : undefined,
            textShadow: isSoft ? '0 1px 8px rgba(10,4,2,0.70)' : undefined,
          }}
        >
          Cyfrowe miejsce pamięci, które przetrwa czas. Twoja historia, wspomnienia i dziedzictwo w jednym miejscu.
        </motion.p>

        <motion.div
          variants={staggerItem}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 flex-wrap"
        >
          <Button
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg btn-theme-glow transition-all"
            asChild
          >
            <Link to={ROUTE_PATHS.CMENTARZ}>Zarezerwuj swoje miejsce</Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="btn-theme-outline px-8 py-6 text-lg"
            asChild
          >
            <Link to={ROUTE_PATHS.IDEA}>Dowiedz się więcej</Link>
          </Button>
          <PWAInstallButton />
        </motion.div>

        <motion.div
          variants={staggerItem}
          className="w-32 h-px bg-gradient-to-r from-transparent via-primary to-transparent mx-auto"
        />
      </motion.div>

      {/* Modal kontaktowy */}
      <AnimatePresence>
        {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}


function IdeaSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  return (
    <section id="idea" className="relative py-32" style={{ background: 'linear-gradient(180deg, var(--background) 0%, var(--secondary) 50%, var(--background) 100%)' }}>
      <div className="absolute inset-0 opacity-8">
        <img
          src={IMAGES.SKY_BG_4}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, color-mix(in oklch, var(--background) 92%, transparent), color-mix(in oklch, var(--background) 88%, transparent))' }} />
      </div>

      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="relative z-10 container mx-auto px-4 max-w-4xl"
      >
        <motion.h2
          variants={staggerItem}
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-16 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-section-heading)' }}
        >
          Każdy z nas odejdzie. Pytanie brzmi: co pozostawi?
        </motion.h2>

        <motion.div variants={staggerItem} className="space-y-8 text-lg md:text-xl text-foreground/80 leading-relaxed">
          <p>
            Śmierć jest nieuchronna. To jedyna pewność w życiu każdego człowieka. Zamiast jej unikać, możemy ją oswoić – zaakceptować jako naturalną część istnienia i przygotować się do niej z godnością.
          </p>
          <p>
            e-grób to cyfrowe miejsce pamięci, gdzie możesz zostawić coś trwałego. Twoje zdjęcia, wspomnienia, przemyślenia, dokumenty – wszystko, co chcesz przekazać przyszłym pokoleniom. To przestrzeń, która przetrwa czas i będzie świadectwem Twojego życia.
          </p>
          <p>
            Dla Twoich bliskich to dar – możliwość powrotu do wspomnień, poznania Twojej historii, poczucia bliskości nawet po latach. To most między życiem a pamięcią, między teraźniejszością a wiecznością.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20"
        >
          {[
            { number: "10,000+", label: "Cyfrowych miejsc" },
            { number: "50 TB", label: "Wspomnień zachowanych" },
            { number: "∞", label: "Lat trwałości" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              variants={staggerItem}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
                {stat.number}
              </div>
              <div className="text-muted-foreground text-sm uppercase tracking-wider">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

function HowItWorksSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="jak-to-dziala" className="relative py-32" style={{ background: 'var(--background)' }}>
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="container mx-auto px-4 max-w-6xl"
      >
        <motion.h2
          variants={staggerItem}
          className="font-serif text-4xl md:text-5xl font-bold text-center mb-20"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-section-heading)' }}
        >
          Jak to działa?
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {HOW_IT_WORKS_STEPS.map((step) => (
            <motion.div
              key={step.number}
              variants={staggerItem}
              className="relative"
            >
              <Card className="p-8 border-border/40 hover:border-primary/40 transition-all duration-500 h-full group" style={{ background: 'var(--card)', boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full border border-primary/50 flex items-center justify-center mb-6 group-hover:border-primary transition-colors duration-300" style={{ background: 'rgba(var(--theme-accent-rgb),0.12)' }}>
                    <span className="text-2xl font-bold text-primary">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {step.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function PlansSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="plany" className="relative py-32" style={{ background: 'linear-gradient(180deg, var(--background) 0%, var(--secondary) 100%)' }}>
      <div className="absolute inset-0 opacity-5">
        <img
          src={IMAGES.GRAVE_STONE_6}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="relative z-10 container mx-auto px-4 max-w-7xl"
      >
        <motion.h2
          variants={staggerItem}
          className="font-serif text-4xl md:text-5xl font-bold text-center mb-20"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-section-heading)' }}
        >
          Wybierz swój plan
        </motion.h2>

<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PLANS.map((plan, index) => {
            // Plan Free (index 0) jest aktywny; Podstawowy i Premium — wkrótce
            const isComingSoon = index > 0;
            return (
              <motion.div
                key={index}
                variants={staggerItem}
                className="relative"
                style={{ opacity: isComingSoon ? 0.5 : 1 }}
              >
                <Card
                  className={`p-8 h-full flex flex-col ${
                    plan.highlighted && !isComingSoon
                      ? "border-2 border-primary shadow-2xl shadow-primary/20 bg-card"
                      : "bg-card/50 backdrop-blur border-border"
                  } transition-all duration-300 ${!isComingSoon ? "hover:scale-105" : "cursor-not-allowed"}`}
                  style={{ filter: isComingSoon ? "grayscale(0.3)" : "none" }}
                >
                  {/* Badge */}
                  {isComingSoon ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full font-medium whitespace-nowrap"
                        style={{ background: "var(--theme-card-bg-deep)", color: "var(--theme-text-dim)", border: "1px solid var(--theme-panel-inner-border)", boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                        Wkrótce dostępny
                      </span>
                    </div>
                  ) : plan.badge ? (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1">
                      {plan.badge}
                    </Badge>
                  ) : null}

                  <div className="text-center mb-8">
                    <h3 className={`text-2xl font-bold mb-2 ${isComingSoon ? "text-foreground/40" : "text-foreground"}`}>
                      {plan.name}
                    </h3>
                    <div className={`text-4xl font-bold mb-2 ${isComingSoon ? "text-muted-foreground/40" : "text-primary"}`}>
                      {plan.price}
                    </div>
                    <div className="text-muted-foreground">{plan.storage}</div>
                  </div>

                  <ul className="space-y-4 mb-8 flex-grow">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isComingSoon ? "text-muted-foreground/30" : "text-primary"}`} />
                        <span className={isComingSoon ? "text-foreground/35" : "text-foreground/80"}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isComingSoon ? (
                    <div className="space-y-2">
                      <div
                        className="w-full py-3 rounded-lg text-sm font-medium text-center cursor-not-allowed select-none"
                        style={{
                          background: "var(--theme-card-bg-deep)",
                          color: "var(--theme-text-dim)",
                          border: "1px solid var(--theme-panel-inner-border)",
                        }}
                      >
                        🔒 Funkcja w rozbudowie
                      </div>
                      <p className="text-xs text-center" style={{ color: "var(--theme-text-dim)" }}>
                        Już wkrótce — dziękujemy za cierpliwość
                      </p>
                    </div>
                  ) : (
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      size="lg"
                      asChild
                    >
                      <Link to={ROUTE_PATHS.CMENTARZ}>Wybierz plan</Link>
                    </Button>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}

function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <section id="cta" className="relative py-40 overflow-hidden" style={{ background: 'var(--background)' }}>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(var(--theme-accent-rgb),0.07) 0%, transparent 70%)' }} />
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={fadeInUp}
        transition={springPresets.gentle}
        className="container mx-auto px-4 text-center max-w-4xl"
      >
        <h2
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--theme-section-heading)' }}
        >
          Zacznij budować swoją cyfrową pamięć już dziś
        </h2>
        <p className="text-xl mb-12 max-w-2xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
          Nie czekaj. Każdy dzień to okazja, by zostawić coś wartościowego dla przyszłych pokoleń.
        </p>
        <Button
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-4 text-lg shadow-2xl transition-all duration-300"
          style={{ boxShadow: '0 0 40px rgba(var(--theme-accent-rgb),0.28)' }}
          asChild
        >
          <Link to={ROUTE_PATHS.CMENTARZ}>Zarezerwuj swoje miejsce</Link>
        </Button>
      </motion.div>
    </section>
  );
}

export default function Home() {
  return (
    <Layout>
      <HeroSection />
      <IdeaSection />
      <HowItWorksSection />
      <PlansSection />
      <CTASection />
    </Layout>
  );
}